<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: school.name,
                        href: null,
                    },
                    {
                        name: group.name,
                        href: null,
                    },
                    {
                        name: $t('messages.sign_up'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.store" method="post" @submit.prevent="submit" v-if="meta.hasValidSignature">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.name') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.first_name')" for="first_name" />
                            <FormInput
                                required
                                id="first_name"
                                v-model.trim="form.first_name"
                                name="first_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.first_name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.nickname')" for="nickname" />
                            <FormInput
                                id="nickname"
                                v-model.trim="form.name"
                                name="nickname"
                                type="text"
                                max="50"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.first_name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.last_name')" for="last_name" />
                            <FormInput
                                required
                                id="last_name"
                                v-model.trim="form.last_name"
                                name="last_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.last_name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.second_last_name')" for="second_last_name" />
                            <FormInput
                                id="second_last_name"
                                v-model.trim="form.second_last_name"
                                name="second_last_name"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.second_last_name" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.date_of_birth')" for="date_of_birth" />
                            <FormInput
                                id="date_of_birth"
                                v-model="form.date_of_birth"
                                name="date_of_birth"
                                type="date"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.date_of_birth" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.gender')" for="gender" />
                            <FormSelect
                                required
                                id="gender"
                                v-model="form.gender"
                                :options="options.genders"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.gender" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.settings') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.preferred_language')" for="preferred_language" />
                            <FormSelect
                                id="preferred_language"
                                v-model="form.preferred_language"
                                :options="options.preferred_languages"
                                optgroups
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.preferred_language" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.contact_info') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.email')" for="email" />
                            <FormInput
                                required
                                id="email"
                                v-model="form.email"
                                name="email"
                                type="email"
                                max="191"
                                autocomplete="off"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.email" class="mt-2" />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.password') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.password')" for="password" />
                            <FormInput
                                required
                                id="password"
                                v-model="form.password"
                                name="password"
                                autocomplete="new-password"
                                type="password"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.password" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.password_repeat')" for="password_confirmation" />
                            <FormInput
                                required
                                id="password_confirmation"
                                v-model="form.password_confirmation"
                                name="password_confirmation"
                                autocomplete="new-password"
                                type="password"
                                max="191"
                                class="mt-1 w-full"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <SeeMore>
                <template v-slot:content>
                    <TitleFieldsFormSection>
                        <template v-slot:title>
                            {{ $t('messages.other') }}
                        </template>

                        <div class="md:grid md:grid-cols-12 md:gap-6">
                            <div class="sm:col-span-6">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.mother_tongue')" for="mother_tongue" />
                                    <FormSelectFilterablePanel
                                        id="mother_tongue"
                                        v-model="form.mother_tongue"
                                        :options="options.languages"
                                        optgroups
                                        class="mt-1 w-full"
                                    />
                                    <FormError :value="form.errors.mother_tongue" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-6">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.nationality')" for="nationality" />
                                    <FormSelectFilterablePanel
                                        id="nationality"
                                        v-model="form.nationality"
                                        :options="options.countries"
                                        optgroups
                                        class="mt-1 w-full"
                                    />
                                    <FormError :value="form.errors.nationality" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-6">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.country_of_origin')" for="country_of_origin" />
                                    <FormSelectFilterablePanel
                                        id="country_of_origin"
                                        v-model="form.country_of_origin"
                                        :options="options.countries"
                                        optgroups
                                        class="mt-1 w-full"
                                    />
                                    <FormError :value="form.errors.country_of_origin" class="mt-2" />
                                </div>
                            </div>
                        </div>
                    </TitleFieldsFormSection>
                </template>
            </SeeMore>

            <div class="flex justify-end mt-5 space-x-2">
                <div>
                    <FormCheckbox v-model="form.terms_of_service">
                        <a :href="meta.links.terms_of_service" target="_blank" class="text-indigo-600">
                            {{ $t('messages.read_and_accept_terms_of_service') }}
                        </a>
                        <FormError :value="form.errors.terms_of_service" class="mt-2" />
                    </FormCheckbox>
                </div>

                <SubmitFormButton :button-text="$t('messages.create')" :processing="form.processing" />
            </div>
        </form>
        <div v-else>
            <h3 class="text-lg">
                {{ $t('messages.registration_is_not_currently_open') }}
            </h3>
            <EmptyState picture>
                <template v-slot:picture />
            </EmptyState>
        </div>
    </PanelPage>
</template>

<script>
import RegisterLayout from '@/Shared/RegisterLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormCheckbox from '@/components/Forms/FormCheckbox.vue';
import EmptyState from '@/components/EmptyState.vue';
import FormSelectFilterablePanel from '@/components/Forms/FormSelectFilterablePanel.vue';
import SeeMore from '@/components/SeeMore.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormCheckbox,
        EmptyState,
        FormSelectFilterablePanel,
        SeeMore,
    },
    layout: RegisterLayout,
    name: 'Edit',
    props: ['meta', 'options', 'school', 'group', 'defaults'],
    data() {
        return {
            form: this.$inertia.form({
                terms_of_service: false,
                first_name: '',
                name: '',
                last_name: '',
                second_last_name: '',
                date_of_birth: '',
                email: '',
                gender: '',
                mother_tongue: this.school.locale,
                nationality: 'ES',
                country_of_origin: 'ES',
                password: '',
                password_confirmation: '',
                preferred_language: this.school.locale,
            }),
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
    },
};
</script>
